import XLSX from "xlsx-js-style";
import moment from "moment";
import { toast } from "../../common_components/Toast/ToastUtils";
// Same look as the Divergence Report export (grey theme, current row + compared row, % CHANGE next to every metric)
const BORDER = { style: "thin", color: { rgb: "E5E7EB" } };
const BORDER_STRONG = { style: "thin", color: { rgb: "D1D5DB" } };
const allBorders = (b) => ({ top: b, bottom: b, left: b, right: b });
const titleStyle = {
    font: { bold: true, sz: 20, color: { rgb: "181D27" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: { bottom: { style: "thin", color: { rgb: "E5E7EB" } } },
};
const subTitleStyle = {
    font: { bold: true, sz: 14, color: { rgb: "374151" } },
    alignment: { horizontal: "center", vertical: "center" },
};
const dateRangeStyle = {
    font: { bold: false, sz: 12, color: { rgb: "6B7280" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: { bottom: { style: "medium", color: { rgb: "D1D5DB" } } },
};
const sectionHeaderStyle = {
    font: { bold: true, sz: 12, color: { rgb: "181D27" } },
    fill: { fgColor: { rgb: "E5E7EB" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: allBorders(BORDER_STRONG),
};
const columnHeaderStyle = {
    font: { bold: true, sz: 11, color: { rgb: "374151" } },
    fill: { fgColor: { rgb: "F3F4F6" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: allBorders(BORDER_STRONG),
};
const currentRowStyle = {
    font: { bold: false, sz: 11, color: { rgb: "181D27" } },
    fill: { fgColor: { rgb: "FFFFFF" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: allBorders(BORDER),
};
const comparedRowStyle = {
    font: { bold: false, sz: 10, color: { rgb: "6B7280" } },
    fill: { fgColor: { rgb: "F9FAFB" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: allBorders(BORDER),
};
const nameColumnStyle = {
    ...currentRowStyle,
    font: { bold: true, sz: 11, color: { rgb: "181D27" } },
    border: { ...allBorders(BORDER), right: { style: "medium", color: { rgb: "D1D5DB" } } },
};
const comparedNameStyle = {
    ...comparedRowStyle,
    border: { ...allBorders(BORDER), right: { style: "medium", color: { rgb: "D1D5DB" } } },
};
const summaryRowStyle = {
    font: { bold: true, sz: 11, color: { rgb: "181D27" } },
    fill: { fgColor: { rgb: "F3F4F6" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: {
        top: { style: "medium", color: { rgb: "D1D5DB" } },
        bottom: { style: "medium", color: { rgb: "D1D5DB" } },
        left: BORDER_STRONG,
        right: BORDER_STRONG,
    },
};
const summaryComparedStyle = {
    font: { bold: true, sz: 10, color: { rgb: "6B7280" } },
    fill: { fgColor: { rgb: "F9FAFB" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: {
        top: BORDER_STRONG,
        bottom: { style: "medium", color: { rgb: "D1D5DB" } },
        left: BORDER_STRONG,
        right: BORDER_STRONG,
    },
};
const emptyStyle = { fill: { fgColor: { rgb: "FFFFFF" } } };
// % change cell: green when up, red when down (the arrow keeps the direction readable without the colour)
const changeStyle = (base, change) => ({
    ...base,
    font: { ...base.font, color: { rgb: change >= 0 ? "16A34A" : "DC2626" } },
});
const formatChange = (change) => {
    if (change === undefined || change === null || Number.isNaN(change))
        return "-";
    return `${change > 0 ? "▲ " : change < 0 ? "▼ " : ""}${Math.abs(change).toFixed(0)}%`;
};
const METRICS = [
    { key: "placements", label: "PLACEMENTS", kind: "count" },
    { key: "clicks", label: "CLICKS", kind: "count" },
    { key: "revenue", label: "REVENUE", kind: "currency" },
    { key: "orders", label: "ORDERS", kind: "count" },
    { key: "spend", label: "SPEND", kind: "currency" },
    { key: "roas", label: "ROAS", kind: "ratio" },
];
const NUMBER_FORMAT = {
    count: "#,##0",
    currency: '"$"#,##0.00',
    ratio: "0.00",
};
const cap = (key) => key.charAt(0).toUpperCase() + key.slice(1);
const text = (v, s) => ({ v, t: "s", s });
const num = (v, kind, s) => ({ v, t: "n", s, z: NUMBER_FORMAT[kind] });
export const exportPublisherReportingExcel = (rows, summary, options) => {
    if (!rows || !rows.length) {
        toast.info("No data available to export");
        return;
    }
    try {
        const isClientView = options.view === "client";
        const hasComparison = Boolean(options.compareStartDate && options.compareEndDate);
        // Leading columns: name, active count (+ funnel for publishers), then VALUE + % CHANGE per metric
        const leading = isClientView
            ? [{ label: "CLIENT", width: 28 }, { label: "ACTIVE PUBLISHERS", width: 20 }]
            : [{ label: "PUBLISHER", width: 28 }, { label: "ACTIVE CLIENTS", width: 16 }, { label: "FUNNEL", width: 12 }];
        const maxColumns = leading.length + METRICS.length * 2;
        const allRows = [];
        const merges = [];
        const fullRow = (first, s) => {
            const row = [];
            for (let i = 0; i < maxColumns; i++)
                row.push(text(i === 0 ? first : "", s));
            return row;
        };
        const pushMerged = (first, s) => {
            merges.push({ s: { r: allRows.length, c: 0 }, e: { r: allRows.length, c: maxColumns - 1 } });
            allRows.push(fullRow(first, s));
        };
        // Title block
        pushMerged("Publisher Reporting", titleStyle);
        pushMerged(isClientView ? "Client Performance" : "Publisher Performance", subTitleStyle);
        let dateRangeText = "";
        if (options.startDate && options.endDate) {
            dateRangeText = `${moment(options.startDate).format("MM/DD/YYYY")} - ${moment(options.endDate).format("MM/DD/YYYY")}`;
            if (hasComparison) {
                dateRangeText += `  vs.  ${moment(options.compareStartDate).format("MM/DD/YYYY")} - ${moment(options.compareEndDate).format("MM/DD/YYYY")}`;
            }
        }
        pushMerged(dateRangeText, dateRangeStyle);
        allRows.push(fullRow("", emptyStyle));
        // Section header + column headers
        pushMerged(`${isClientView ? "Clients" : "Publishers"} (${rows.length} selected)`, sectionHeaderStyle);
        const headerRow = leading.map((c, i) => text(c.label, i === 0 ? { ...columnHeaderStyle, alignment: { horizontal: "left", vertical: "center" } } : columnHeaderStyle));
        METRICS.forEach((m) => {
            headerRow.push(text(m.label, columnHeaderStyle));
            headerRow.push(text("% CHANGE", columnHeaderStyle));
        });
        allRows.push(headerRow);
        // One block per selected row: current row (+ % change), then the compared-period row
        const buildBlock = (source, summaryLabel, styles, isSummary) => {
            const leadingCells = (isCompared) => {
                if (isSummary) {
                    return leading.map((_, i) => text(i === 0 && !isCompared ? summaryLabel : "", isCompared ? styles.comparedName : styles.name));
                }
                if (isCompared)
                    return leading.map((_, i) => text(i === 0 ? "Comparison Period" : "", styles.comparedName));
                return [
                    text(isClientView ? source.clientName || "-" : source.publisherName || "-", styles.name),
                    num(source.publisherCount ?? 0, "count", styles.current),
                    ...(isClientView ? [] : [text(source.funnel || "-", styles.current)]),
                ];
            };
            const currentRow = leadingCells(false);
            METRICS.forEach((m) => {
                const value = Number(source[isSummary ? `total${cap(m.key)}` : m.key] ?? 0);
                currentRow.push(num(value, m.kind, styles.current));
                const diff = source[isSummary ? `total${cap(m.key)}Diff` : `${m.key}Diff`];
                currentRow.push(hasComparison
                    ? text(formatChange(diff), diff === undefined || diff === null ? styles.current : changeStyle(styles.current, diff))
                    : text("", styles.current));
            });
            allRows.push(currentRow);
            if (hasComparison) {
                const comparedRow = leadingCells(true);
                METRICS.forEach((m) => {
                    const value = Number(source[isSummary ? `totalCompare${cap(m.key)}` : `compare${cap(m.key)}`] ?? 0);
                    comparedRow.push(num(value, m.kind, styles.comparedValue));
                    comparedRow.push(text("", styles.comparedValue));
                });
                allRows.push(comparedRow);
            }
        };
        const rowStyles = {
            name: nameColumnStyle,
            current: currentRowStyle,
            comparedName: comparedNameStyle,
            comparedValue: comparedRowStyle,
        };
        rows.forEach((row) => buildBlock(row, "", rowStyles, false));
        // Total Summary of the selected rows
        if (summary) {
            buildBlock(summary, "Total Summary", {
                name: summaryRowStyle,
                current: summaryRowStyle,
                comparedName: summaryComparedStyle,
                comparedValue: summaryComparedStyle,
            }, true);
        }
        // Build the sheet
        const worksheet = {};
        allRows.forEach((row, r) => {
            row.forEach((cell, c) => {
                worksheet[XLSX.utils.encode_cell({ r, c })] = cell;
            });
        });
        worksheet["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: allRows.length - 1, c: maxColumns - 1 } });
        worksheet["!merges"] = merges;
        const colWidths = leading.map((c) => ({ wch: c.width }));
        METRICS.forEach((m) => {
            colWidths.push({ wch: m.kind === "currency" ? 20 : 14 });
            colWidths.push({ wch: 12 });
        });
        worksheet["!cols"] = colWidths;
        worksheet["!rows"] = allRows.map((_, i) => ({ hpt: i === 0 ? 35 : i === 1 ? 26 : i === 2 ? 22 : i === 3 ? 15 : 22 }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, isClientView ? "Client Performance" : "Publisher Performance");
        const fileName = `Publisher_Reporting_${isClientView ? "By_Client" : "By_Publisher"}_${moment().format("YYYY-MM-DD_HH-mm")}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    }
    catch (error) {
        console.error("Export failed:", error);
        toast.info("Failed to export data. Please try again.");
    }
};
// Shared with the drawer export (exportPublisherReportingDrawerExcel.ts) so both files look the same
export { titleStyle, subTitleStyle, dateRangeStyle, sectionHeaderStyle, columnHeaderStyle, currentRowStyle, comparedRowStyle, nameColumnStyle, comparedNameStyle, summaryRowStyle, summaryComparedStyle, emptyStyle, changeStyle, formatChange, text, num, cap, NUMBER_FORMAT, };
export default exportPublisherReportingExcel;
